<script lang="ts" setup>
import { useUnmanagedBusinessStore } from '@/unmanagedBusinessModule/pages/unmanagedBusinessStore';
import { storeToRefs } from 'pinia';
import { i18n } from '@/i18n';
import TextInput from './TextInput.vue';
import MultiTagInput from '@/corePagesModule/components/MultiTagInput.vue';
import InfoModal from '@/corePagesModule/components/InfoModal.vue';
import { ImportFileType } from '@/coreDomainsModule/entities/unmanagedBusiness';
import SelectInput from '@/corePagesModule/SelectInput/SelectInput.vue';
import SimpleSelectInput from '@/corePagesModule/components/SelectInput.vue';
import { computed, ref, watch } from 'vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { selectOption, userInputOption, inputTypeKeyMap, selectOptionKeyMap, validateRuleKeyMap } from '../constants/formKeyMap';
import { UnmanagedBusinessService } from '../domains/services/UnmanagedBusinessService';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import AddOptionSection from '@/corePagesModule/updateBusinessModal/components/AddOptionSection.vue';
import { diContainer } from '@/coreContainer/container';
import { SettingRepository } from '@/coreInfrastructureModule/Repositories/SettingRepository';
import { BusinessFreeColumnValue } from '@/coreDomainsModule/entities/businessBase';

const t = i18n.t;

const store = useUnmanagedBusinessStore();
const { state } = storeToRefs(store);
const { closeModal, openModalByPreviousModal, convertKey, getAddableOptions } = store;

const currentOpenSelectInputName = ref('');

/**
 * APIの構造的に、不必要なリクエストを送らないようにするために行なっている(codeとnameは一対一対応なので片方で埋まっていればもう片方で叩く必要はない)
 * STOPSHIP: SuccessにフックさせているがそもそもAPIの仕様がおかしい気がするのでそこは別途用対応（経緯：https://codeyhq.slack.com/archives/C06T7DCL41L/p1745070581836609?thread_ts=1743985608.078519&cid=C06T7DCL41L）
 */
const firstAutoSetParams = {
  business: false,
  warehouse: false,
  supplier: false,
  customer: false,
};

const settingRepository = new SettingRepository();

/**
 * 編集中の自由欄値の作業用ref
 * importFileType に応じて、集計のみ、または集計+関連の値を保持する（columnNo は集計・関連それぞれ 1 から採番）
 */
const editingBusinessFreeColumnValues = ref<BusinessFreeColumnValue[]>([]);

const filedItems = computed((): FieldItem[] => {
  // 選択肢の項目を取得
  const items = selectOption[state.value.selectedFileType].map((name) => {
    return {
      name,
      rules: validateRuleKeyMap[name],
    };
  });
  // 入力項目を取得
  if (state.value.selectedFileType === ImportFileType.Arrival || state.value.selectedFileType === ImportFileType.Inventory) {
    userInputOption.forEach((item) => {
      items.push({
        name: item,
        rules: validateRuleKeyMap[item],
      });
    });
  }
  return items;
});

const { fieldState, allValidate, resetForm } = useForms(filedItems.value);

/**
 * 次へボタンクリック時の処理
 */
const onNext = () => {
  if (allValidate()) {
    filedItems.value.forEach((item) => {
      // リードタイム単位と標準在庫指数単位はconvertUnitToSystemUnitで変換する
      if (item.name === 'leadTimeUnit' || item.name === 'stdInventoryIndicesUnit') {
        state.value.modalInfo.modalState.registerBusiness[item.name] = UnmanagedBusinessService.convertUnitToSystemUnit(
          fieldState[item.name].value.value
        );
        return;
      }
      state.value.modalInfo.modalState.registerBusiness[item.name] = fieldState[item.name].value.value;
    });
    state.value.modalInfo.modalState.registerBusiness.businessFreeColumnValues = editingBusinessFreeColumnValues.value.map((item) => ({
      ...item,
    }));
    openModalByPreviousModal('registerBusiness');
  }
};

/**
 * 戻るボタンクリック時の処理
 * モーダルを閉じて、入力項目の値をリセットする
 * また、最初に自動で埋めた項目のフラグをリセットする
 * これにより、次回モーダルを開いた際に再度自動で埋めることができるようにする
 */
const onClose = () => {
  closeModal('registerBusiness');
  resetForm(filedItems.value);
  currentOpenSelectInputName.value = '';
  firstAutoSetParams.business = false;
  firstAutoSetParams.warehouse = false;
  firstAutoSetParams.supplier = false;
  firstAutoSetParams.customer = false;
};

watch(
  () => state.value.modalInfo.isOpen.registerBusiness,
  async (value) => {
    if (value) {
      resetForm(filedItems.value);
      await initializeBusinessFreeColumnValues();
      // selectedBusinessの値をfieldStateにセット
      filedItems.value.forEach((item) => {
        if (
          state.value.modalInfo.modalState.registerBusiness[item.name] === '' ||
          state.value.modalInfo.modalState.registerBusiness[item.name] === 0
        )
          return;
        if (item.name === 'leadTimeUnit' || item.name === 'stdInventoryIndicesUnit') {
          fieldState[item.name].setValue(
            UnmanagedBusinessService.convertSystemUnitToDisplayUnit(state.value.modalInfo.modalState.registerBusiness[item.name]),
            false
          );
          return;
        }
        fieldState[item.name].setValue(state.value.modalInfo.modalState.registerBusiness[item.name], false);
      });

      // name優先で自動で埋めていく
      for (const item of filedItems.value) {
        if (item.name === 'businessNumber' && !firstAutoSetParams.business) {
          await setBusinessName();
        } else if (item.name === 'warehouseNumber' && !firstAutoSetParams.warehouse) {
          await setWarehouseName();
        } else if (item.name === 'supplierNumber' && !firstAutoSetParams.supplier) {
          await setSupplierName();
        } else if (item.name === 'customerNumber' && !firstAutoSetParams.customer) {
          await setCustomerName();
        }
      }

      // 次はcodeを自動で埋めていく
      filedItems.value.forEach((item) => {
        if (item.name === 'businessName' && !firstAutoSetParams.business) {
          setBusinessNumber();
        } else if (item.name === 'warehouseName' && !firstAutoSetParams.warehouse) {
          setWarehouseNumber();
        } else if (item.name === 'supplierName' && !firstAutoSetParams.supplier) {
          setSupplierNumber();
        } else if (item.name === 'customerName' && !firstAutoSetParams.customer) {
          setCustomerNumber();
        }
      });
    }
  }
);

const selectionClass = computed(() => {
  return state.value.selectedFileType === ImportFileType.Arrival || state.value.selectedFileType === ImportFileType.Inventory
    ? 'modal__selection--wide'
    : '';
});

/**
 * 入力項目の値をセットする
 * @param key - 入力項目のキー
 * @param value - 入力値
 */
const setValue = (key: string, value: string) => {
  fieldState[key].setValue(value);
  const keyMap = {
    businessNumber: setBusinessName,
    businessName: setBusinessNumber,
    warehouseNumber: setWarehouseName,
    warehouseName: setWarehouseNumber,
    supplierNumber: setSupplierName,
    supplierName: setSupplierNumber,
    customerNumber: setCustomerName,
    customerName: setCustomerNumber,
  };
  if (keyMap[key]) keyMap[key]();
};

/**
 * 汎用的な値設定関数
 * @param useCase - 使用するユースケース
 * @param sourceKey - 取得元のキー
 * @param targetKey - 設定先のキー
 */
const setFieldValue = async (useCase, sourceKey, targetKey) => {
  if (fieldState[sourceKey].value.value === '') return; // 空文字（選択を外す）なら何もしない

  const result: INetworkResult<string | undefined, IError> = await useCase.execute(fieldState[sourceKey].value.value);

  // マッチする値が存在しない場合は何もしない
  if (result.status.type === 'success' && result.status.data !== undefined) {
    fieldState[targetKey].setValue(result.status.data);
    setFirstAutoSetParams(targetKey);
  }
};

/** 商品名を設定する */
const setBusinessName = () => setFieldValue(diContainer.getBusinessNameUseCase, 'businessNumber', 'businessName');
/** 商品番号を設定する */
const setBusinessNumber = () => setFieldValue(diContainer.getBusinessNumberUseCase, 'businessName', 'businessNumber');
/** 置き場名を設定する */
const setWarehouseName = () => setFieldValue(diContainer.getWarehouseNameUseCase, 'warehouseNumber', 'warehouseName');
/** 置き場番号を設定する */
const setWarehouseNumber = () => setFieldValue(diContainer.getWarehouseNumberUseCase, 'warehouseName', 'warehouseNumber');
/** 仕入先名を設定する */
const setSupplierName = () => setFieldValue(diContainer.getSupplierNameUseCase, 'supplierNumber', 'supplierName');
/** 仕入先番号を設定する */
const setSupplierNumber = () => setFieldValue(diContainer.getSupplierNumberUseCase, 'supplierName', 'supplierNumber');
/** 売り上げ先名を設定する */
const setCustomerName = () => setFieldValue(diContainer.getCustomerNameUseCase, 'customerNumber', 'customerName');
/** 売り上げ先番号を設定する */
const setCustomerNumber = () => setFieldValue(diContainer.getCustomerNumberUseCase, 'customerName', 'customerNumber');

/**
 * バリデーションにRequiredがある場合は*を返す
 * @param key　- 入力項目のキー
 */
const getRequiredMark = (key: string | number) => {
  if (validateRuleKeyMap[key].includes(ValidateRule.Required)) {
    return '*';
  }
  return '';
};

/**
 * setter
 * @param targetKey - 自動埋めが成功したキー
 */
const setFirstAutoSetParams = (targetKey: string) => {
  switch (targetKey) {
    case 'businessName':
      firstAutoSetParams.business = true;
      break;
    case 'businessNumber':
      firstAutoSetParams.business = true;
      break;
    case 'warehouseName':
      firstAutoSetParams.warehouse = true;
      break;
    case 'warehouseNumber':
      firstAutoSetParams.warehouse = true;
      break;
    case 'supplierName':
      firstAutoSetParams.supplier = true;
      break;
    case 'supplierNumber':
      firstAutoSetParams.supplier = true;
      break;
    case 'customerName':
      firstAutoSetParams.customer = true;
      break;
    case 'customerNumber':
      firstAutoSetParams.customer = true;
      break;
    default:
      break;
  }
};

/**
 * 新規選択肢追加ボタンを押した際の処理
 * @param type - 選択肢の種類
 * @param name - 名前
 * @param code - コード(Package, Manufacturer, EndUserはなし)
 */
const addNewOption = (type: string, name: string, code?: string) => {
  switch (type) {
    case 'package':
    case 'manufacturer':
    case 'endUser':
      fieldState[type].setValue(name);
      break;
    default:
      if (!code) {
        console.error('エラー:未管理>登録>新規選択肢追加:Codeなし');
        state.value.modalInfo.isOpen.error = true;
        return;
      }
      fieldState[type + 'Name'].setValue(name);
      fieldState[type + 'Number'].setValue(code);
  }

  return;
};

/**
 * importFileType によって、関連取引の自由欄も表示する対象かどうかを返す
 * arrival/inventory では関連取引が生成されないため、集計のみ表示する
 */
const includesBaseFreeColumns = computed(() => {
  return state.value.selectedFileType === ImportFileType.SalesResult || state.value.selectedFileType === ImportFileType.Announce;
});

/**
 * 自由欄値の入力時に作業用refを更新する
 * @param index - 自由欄のindex
 * @param value - 入力値
 */
const updateBusinessFreeColumnValue = (index: number, value: string) => {
  if (editingBusinessFreeColumnValues.value[index]) {
    editingBusinessFreeColumnValues.value[index].value = value;
  }
};

/**
 * モーダルを開いた際に自由欄値を初期化する
 * 設定APIから取得した集計・関連の自由欄を、それぞれ columnNo 1 から採番して target 付きで保持する
 * importFileType に応じて表示する自由欄を絞り込む
 * 設定ページで保存済み（isConfigured=true）の項目のみを表示する
 * 既に modalState に値がある場合（前回入力など）はそれで上書きする
 */
const initializeBusinessFreeColumnValues = async () => {
  const result = await settingRepository.getBusinessFreeColumnSetting();
  if (result.status.type !== 'success') {
    editingBusinessFreeColumnValues.value = [];
    return;
  }
  const setting = result.status.data;
  const aggregateValues: BusinessFreeColumnValue[] = setting.aggregate
    .map((item, index) => ({
      columnNo: index + 1,
      target: 'aggregate' as const,
      headerName: item.headerName,
      isMulti: item.isMulti,
      isConfigured: item.isConfigured ?? false,
      value: '',
    }))
    .filter((item) => item.isConfigured);
  const baseValues: BusinessFreeColumnValue[] = includesBaseFreeColumns.value
    ? setting.base
        .map((item, index) => ({
          columnNo: index + 1,
          target: 'base' as const,
          headerName: item.headerName,
          isMulti: item.isMulti,
          isConfigured: item.isConfigured ?? false,
          value: '',
        }))
        .filter((item) => item.isConfigured)
    : [];
  const merged = [...aggregateValues, ...baseValues];
  const existingValues = state.value.modalInfo.modalState.registerBusiness.businessFreeColumnValues ?? [];
  // columnNo は集計・関連それぞれ 1 から採番されるため、target と組み合わせてキーにする
  const existingValueMap = new Map(existingValues.map((item) => [`${item.target}-${item.columnNo}`, item.value]));
  editingBusinessFreeColumnValues.value = merged.map((item) => ({
    ...item,
    value: existingValueMap.get(`${item.target}-${item.columnNo}`) ?? '',
  }));
};
</script>
<template>
  <InfoModal
    :modal-state="state.modalInfo.isOpen.registerBusiness"
    :back-button-label="t('戻る')"
    :next-button-label="t('次へ')"
    @on-back="onClose"
    @on-next="onNext"
  >
    <template #title>{{ t('必須項目に追記して取引登録') }}</template>
    <template #description>{{
      t('既存登録項目に修正し登録してください。選択肢が存在しない場合は新規選択肢を追加して登録してください。')
    }}</template>
    <div class="modal__required-text">
      <p>{{ t('*マークは必須') }}</p>
      <div :class="['modal__selection', selectionClass]">
        <template v-for="(value, key, index) in state.tableInfo[state.selectedFileType].selectOptionInfo" :key="key">
          <template v-if="inputTypeKeyMap[key] === 'select'">
            <SelectInput
              :model-value="fieldState[key].value.value"
              :filter-type="convertKey(key.toString())"
              :index="index"
              :label="t(`取引登録モーダル選択肢:${key}`) + getRequiredMark(key)"
              :placeholder="t(`取引登録モーダル選択肢:${key}`)"
              :is-open-prop="currentOpenSelectInputName === key"
              :is-error="fieldState[key].isError.value"
              :error-message="fieldState[key].errorMessage.value"
              :prop-options="state.tableInfo[state.selectedFileType].selectOptionInfo[key]"
              @click:select-input="() => (currentOpenSelectInputName = key.toString())"
              @update:model-value="
                (e) => {
                  setValue(key.toString(), e);
                }
              "
            />
          </template>
          <template v-else-if="inputTypeKeyMap[key] === 'text'">
            <TextInput
              :model-value="fieldState[key].value.value"
              :label="t(`取引登録モーダル選択肢:${key}`) + getRequiredMark(key)"
              :placeholder="t(`取引登録モーダル選択肢:${key}`)"
              :is-error="fieldState[key].isError.value"
              :error-message="fieldState[key].errorMessage.value"
              @update:model-value="(e) => fieldState[key].setValue(e)"
            />
          </template>
        </template>
      </div>
    </div>
    <template v-if="state.selectedFileType === ImportFileType.Arrival || state.selectedFileType === ImportFileType.Inventory">
      <div class="modal__selection modal__selection-user-input">
        <template v-for="(value, key) in userInputOption" :key="key">
          <template v-if="inputTypeKeyMap[value] === 'select'">
            <SimpleSelectInput
              :model-value="fieldState[value].value.value"
              :index="key + 100"
              :label="t(`取引登録モーダル選択肢:${value}`) + getRequiredMark(value)"
              :placeholder="t(`取引登録モーダル選択肢:${value}`)"
              :options="selectOptionKeyMap[value]?.map((item) => t(item)) ?? []"
              :is-open-prop="currentOpenSelectInputName === value"
              :is-error="fieldState[value].isError.value"
              :error-message="fieldState[value].errorMessage.value"
              @click:select-input="() => (currentOpenSelectInputName = value)"
              @update:model-value="(e) => fieldState[value].setValue(e)"
            />
          </template>
          <template v-else-if="inputTypeKeyMap[value] === 'text'">
            <TextInput
              :model-value="fieldState[value].value.value"
              :label="t(`取引登録モーダル選択肢:${value}`) + getRequiredMark(value)"
              :placeholder="t(`取引登録モーダル選択肢:${value}`)"
              :is-error="fieldState[value].isError.value"
              :error-message="fieldState[value].errorMessage.value"
              @update:model-value="(e) => fieldState[value].setValue(e)"
            />
          </template>
        </template>
      </div>
    </template>
    <!-- 新規選択肢追加 -->
    <AddOptionSection :options="getAddableOptions()" @set-option="addNewOption" />
    <div v-if="editingBusinessFreeColumnValues.length" class="modal__selection modal__selection-free-columns">
      <template v-for="(freeColumn, index) in editingBusinessFreeColumnValues" :key="freeColumn.columnNo">
        <MultiTagInput
          v-if="freeColumn.isMulti"
          :model-value="freeColumn.value"
          :label="freeColumn.headerName"
          :placeholder="freeColumn.headerName"
          @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
        />
        <TextInput
          v-else
          :model-value="freeColumn.value"
          :label="freeColumn.headerName"
          :placeholder="freeColumn.headerName"
          @update:model-value="(e) => updateBusinessFreeColumnValue(index, e)"
        />
      </template>
    </div>
  </InfoModal>
</template>
<style lang="scss" scoped>
.modal {
  display: grid;
  gap: 24px;
  padding: 24px 16px;
  background-color: $semanticWhite;
  border-radius: 16px;
  min-width: 1300px;

  &__required-text {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    text-align: left;
    color: $textDefault;
  }

  &__title {
    font-family: Noto Sans JP;
    font-size: 22px;
    font-weight: 700;
    line-height: 28.6px;
    text-align: center;

    color: $textDefault;
  }

  &__description {
    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    text-align: center;

    color: $textDefault;
  }

  &__selection {
    background-color: $backgroundDefault;
    padding: 16px;
    border-radius: 8px;

    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 8px;
    row-gap: 16px;
    min-width: 1300px;

    // 在庫・入荷の場合の5列表示用クラス
    &--wide {
      grid-template-columns: repeat(5, 1fr);
    }

    &-user-input {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      column-gap: 8px;
      row-gap: 16px;
    }

    &-free-columns {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      column-gap: 8px;
      row-gap: 16px;
    }
  }

  &__new-selection {
    background-color: $backgroundDefault;
    padding: 16px;
    border-radius: 8px;

    font-family: Noto Sans JP;
    font-size: 14px;
    font-weight: 400;
    line-height: 14px;
    text-align: left;

    &__container {
      display: grid;
      grid-template-columns: minmax(160px, auto) auto minmax(160px, auto) auto auto;
      column-gap: 8px;
      justify-content: start;
      align-items: center;
      margin: 8px 0 16px 0;
    }
  }

  &__button {
    min-width: 160px;
    height: 40px;
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 40px;
    letter-spacing: 0em;
    text-align: center;
    border-radius: 4px;
    padding: 0 8px;
    box-shadow: none;

    &--cancel {
      color: $semanticPrimary;
      background-color: $semanticWhite;
      border: 1px solid $semanticPrimary;
    }
    &--save {
      color: $semanticWhite;
      background-color: $surfaceDisable;
      border: 1px solid $surfaceDisable;

      &:disabled {
        background-color: $surfaceSecondary;
        border: 1px solid $surfaceSecondary;
        color: $textDisable;
      }
    }

    &-container {
      display: grid;
      gap: 10px;
      grid-template-columns: repeat(2, auto);
      justify-content: center;
      padding-top: 16px;
    }
  }
}
</style>
